import AppBreadcrumb from "../../components/AppBreadcrumb/AppBreadcrumb"
import documentStyles from "../../styles/documentSigning/DocSign.documents.module.css"
import { Button } from "primereact/button"
import { useContext, useEffect, useState } from "react"
import { DateTime } from "luxon"
import { save } from "save-file"
import HelloSign from "hellosign-embedded"
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr"
import { documentSigningApiUrl } from "../../config/microservicesUrls"

//PrimeReact
import { Paginator } from "primereact/paginator"

//SOAR Components
import { LoaderMedium } from "../../components/Loaders"
import { UserContext } from "../../context/userContext"
import { LogError, LogWarning } from "../../helpers/logger"
import CustomFilter from "../../components/CustomFilter/CustomFilter"
import {
  FilterLocation,
  FilterDefaults,
  FilterTypes,
} from "../../components/CustomFilter/FilterConstants"

//documentSigning imports
import {
  docSigningApiSignatureRequestsDownloadGet,
  docSigningApiSignURLGet,
  docSigningApiSignatureRequestsListPost,
  docSigningApiHelloSignConfigGet,
  docSigningApiRequestGet,
} from "./documentsApi"

//graphQL
import { useLazyQuery } from "@apollo/client"
import { GET_USERS_FROM_LIST } from "./queries"
import NoData from "../../components/NoData"
import StandardPageHeader from "../../components/StandardPageHeader/StandardPageHeader"
import { MICROSERVICE_IDS } from "../../helpers/microserviceConstants"

const defaultFilters = {
  dateCreated: [],
  search: "",
  organizations: [],
  status: "awaiting_signature",
}

const HELLOSIGN_EVENT_TYPE = {
  SIGNATURE_REQUEST_SIGNED: 6,
  SIGNATURE_REQUEST_INVALID: 17,
  SIGN_URL_INVALID: 4,
  UNKNOWN_ERROR: 2,
}

export default function Documents() {
  const userCtx = useContext(UserContext)

  const [appliedFilters, setAppliedFilters] = useState(defaultFilters)
  const [documents, setDocuments] = useState(null)
  const [helloSignConfig, setHelloSignConfig] = useState(undefined)
  const [staffUsers, setStaffUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingDropboxSign, setLoadingDropboxSign] = useState(false)
  const [docFilters, setDocFilters] = useState([])

  // Pagination defaults
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // SignalR
  const [connection, setConnection] = useState(null)
  const isTestMode = process.env.REACT_APP_STAGE === "development"

  const stripMetaKey = (value) => {
    try {
      return value.split("||")[0]
    } catch (error) {
      return value
    }
  }

  const assignedToMe = (request) => {
    let mySignature = undefined
    request.signatures.forEach((signature) => {
      const signerId = stripMetaKey(request.metadata[signature.signerRole])

      if (
        signerId === userCtx.participant_id ||
        signerId === userCtx.participant_alias
      ) {
        mySignature = signature
      }
    })

    return mySignature
  }

  const [get_users_in_list] = useLazyQuery(GET_USERS_FROM_LIST, {
    variables: {},
    fetchPolicy: "network-only",
  })

  const checkUserProfiles = async (requests) => {
    let userIds = []
    requests.forEach((request) => {
      request.signatures.forEach((signature) => {
        userIds.push(
          stripMetaKey(request.metadata[signature.signerRole]).replace(
            "-STAFF",
            "",
          ),
        )
      })
    })

    userIds = userIds.filter((id) => {
      return (
        id !== userCtx.participant_id &&
        !staffUsers.some((staff) => staff.id === id)
      )
    })
    userIds = Array.from(new Set(userIds))

    let staff_users = await get_users_in_list({
      variables: { list: userIds },
    })

    staff_users = staff_users.data.users.filter((user) => {
      return !!user && !!user.staff_id
    })

    setStaffUsers([
      ...staffUsers,
      ...staff_users.map((user) => {
        return {
          ...user,
          signerId: `${user.staff_id}-STAFF`,
          signerAlias: `${user.staff_alias}-STAFF`,
          name: `${user.staff_name_first} ${user.staff_name_last}`,
        }
      }),
    ])
  }

  const getRequests = async (page, pageSize, filters) => {
    setLoading(true)
    try {
      const useFilters = filters || appliedFilters

      // Build request
      const requestBody = {
        groupIds: [
          ...userCtx.participant_organizations.map((x) => x.organization_id),
        ],
        pageNum: page || 1,
        pageSize: pageSize || 10,
      }

      if (useFilters.organizations?.length > 0) {
        requestBody.groupIds = [...useFilters.organizations]
      }

      if (useFilters.dateCreated.length > 0) {
        let eopFilter = !!useFilters.dateCreated[1]
          ? useFilters.dateCreated[1]
          : useFilters.dateCreated[0]
        eopFilter.setHours(23)
        eopFilter.setMinutes(59)
        eopFilter.setSeconds(59)

        requestBody.createDateFilter = [
          DateTime.fromJSDate(useFilters.dateCreated[0]).toUTC().toISO(),
          DateTime.fromJSDate(eopFilter).toUTC().toISO(),
        ]
      }

      if (useFilters.search) {
        requestBody.searchFilter = useFilters.search
      }

      if (useFilters.status) {
        requestBody.statusFilter = useFilters.status
      }

      //Always filter on my participantId or alias
      requestBody.recipientIds = [userCtx.participant_id]

      if (userCtx.participant_alias > 0) {
        requestBody.recipientIds.push(userCtx.participant_alias)
      }

      // Execute request
      let data = await docSigningApiSignatureRequestsListPost(
        userCtx.tokens.getDocumentSignToken(),
        requestBody,
      )

      if (data.items?.length > 0) {
        //Reset structure to expected layout
        data.items = data.items.map((request) => {
          return {
            ...request.signatureRequest,
            numSignersComplete: request.numSignersComplete,
            numSignersTotal: request.numSignersTotal,
          }
        })

        await checkUserProfiles(data.items)
        const getStatus = (request) => {
          let status = "No status available"

          if (request.isComplete) {
            status = "Complete"
          } else if (request.isDeclined) {
            status = "Declined"
          } else if (request.hasError) {
            status = "Error"
          } else if (request.numSignersComplete < request.numSignersTotal) {
            status = `Awaiting signature (${request.numSignersComplete} of ${request.numSignersTotal} complete)`
          }

          return status
        }

        const getOrder = (request) => {
          if (!request.assignedToMe) {
            return "N/A"
          } else {
            return `${request.mySignatureRequest?.order || "0"} ${
              request.mySignatureRequest?.order === 1
                ? "person needs"
                : "people need"
            }
                  to sign before you.`
          }
        }

        data.items.forEach((request) => {
          request.mySignatureRequest = assignedToMe(request)
          request.assignedToMe = !!request.mySignatureRequest
          request.status = getStatus(request)
          request.canSignNow =
            request.assignedToMe &&
            request.numSignersComplete >= request.mySignatureRequest?.order &&
            request.mySignatureRequest.statusCode === "awaiting_signature"
          request.orderText = getOrder(request)
          request.requestCreatorId = stripMetaKey(request?.metadata?.Sender)
          request.requestCreatorName = stripMetaKey(
            request?.metadata?.SenderName,
          )

          //created date value
          request.requestCreatedAt = new Date(
            stripMetaKey(request?.metadata?.CreatedAt),
          )

          //Created display string
          request.requestCreatedAtLocal =
            request.requestCreatedAt.toLocaleString()

          //Date that the request was completed
          request.completedDate =
            request.signatures[0]?.statusCode === "signed"
              ? new Date(request.signatures[0].signedAt * 1000)
              : undefined

          /*
          NOTE: Since JavaScript works with time in milliseconds, 
          you need to multiply the Unix timestamp (which is in seconds) 
          by 1000 before passing it to the Date constructor.
          */

          //Display value of when request was completed
          request.completedDateLocal =
            request.signatures[0]?.statusCode === "signed"
              ? new Date(request.signatures[0].signedAt * 1000).toLocaleString()
              : "Not Completed"

          // Set organization name
          const requestOrg = userCtx.participant_organizations.find(
            (org) =>
              org.organization_id ===
                request.metadata.GroupId?.split("||")[0] ||
              org.organization_alias ===
                request.metadata.GroupId?.split("||")[0],
          )
          request.organizationName = requestOrg?.organization_name || "N/A"
          request.organizationId = requestOrg?.organization_id || null

          // Set signers string
          request.signers = request.signatures
            .map((signature) => {
              const signerId = stripMetaKey(
                request.metadata[signature.signerRole],
              )
              const staffUser = staffUsers.find(
                (user) => user.signerId === signerId,
              )
              return staffUser?.name || `${stripMetaKey(signature.signerName)}`
            })
            .join(", ")
        })
      }

      /*
      Sorting was removed due to DropboxSign not facilitating sorting in their API queries.
      This forced ut to rely on local sorting, which we decided to rather remove altogether.
      I'm keeping this code in case we need to reimplement local-sorting in the future.
      */
      //Sort data
      // data = sortRequests(
      //   useFilters.sort,
      //   useFilters.sortDirection,
      //   data
      // )

      // Set data
      setDocuments(data)
      setLoading(false)

      return data
    } catch (error) {
      LogError("Unable to list requests", error)
      userCtx.setNotificationError(
        "Unable to list signature requests. See console for details.",
      )
      setDocuments([])
      setLoading(false)
    }
  }

  function ValidateRequestGroup(signatureRequest) {
    try {
      if (signatureRequest) {
        // Extract GID
        const tGID = signatureRequest.metadata?.GroupId?.split("||")[0]

        // Compare GID
        if (
          userCtx.participant_organizations?.find(
            (org) => org.organization_id === tGID,
          )
        ) {
          return true
        }
      }
    } catch (error) {
      LogWarning(error)
    }

    return false
  }

  // userKey = array of "Sender" and/or "Signer"
  //   ex.1: ["Sender"]
  //   ex.2: ["Sender", "Signer"]
  //   ex.3: ["Signer"]
  function ValidateRequestUser(signatureRequest, userKey) {
    try {
      if (signatureRequest) {
        for (const key of Object.keys(signatureRequest.metadata)) {
          if (
            signatureRequest.metadata[key].includes(userKey[0]) ||
            signatureRequest.metadata[key].includes(userKey[1])
          ) {
            // Extract ID
            const userId = signatureRequest.metadata[key]
              .split("||")[0]
              ?.replace("-STAFF", "")
            if (userId && userId === userCtx.participant_id) {
              return true
            }
          }
        }
      }
    } catch (error) {
      LogWarning(error)
    }

    return false
  }

  async function GetRequestDetails(signatureRequest) {
    try {
      if (!signatureRequest || !signatureRequest.metadata) {
        return await docSigningApiRequestGet(
          userCtx.tokens.getDocumentSignToken(),
          signatureRequest.signatureRequestId,
        )
      } else {
        return signatureRequest
      }
    } catch (error) {
      LogError("Unable to get signature request details.", error)
      userCtx.setNotificationError(
        "Unable to get signature request details. See console for details.",
      )
    }
  }

  function ApplyFiltersAndSorting() {
    const filters = {
      dateCreated:
        docFilters.find((filter) => filter.key === "date_requested_filter")
          ?.currentValue || [],
      search:
        docFilters.find((filter) => filter.key === "text_search")
          ?.currentValue || "",
      organizations:
        docFilters.find((filter) => filter.key === "organization_filter")
          ?.currentValue || [],
      status:
        docFilters.find((filter) => filter.key === "status_filter")
          ?.currentValue || "all",
    }

    setAppliedFilters(filters)
    getRequests(1, 10, filters)
  }

  async function handleHelloSignEvent(data) {
    try {
      const dataObj = typeof data === "string" ? JSON.parse(data) : data
      const signatureRequest = await GetRequestDetails(
        dataObj?.signatureRequest,
      )

      if (!signatureRequest) {
        // The request details could not be fetched or were invalid.
        // The error is already logged within GetRequestDetails.
        return
      }

      const validatedRequestGroup = ValidateRequestGroup(signatureRequest)
      const validatedRequestUser = ValidateRequestUser(signatureRequest, [
        "Signer",
      ])

      if (validatedRequestGroup && validatedRequestUser) {
        // eslint-disable-next-line default-case
        switch (dataObj?.event?.eventType) {
          case HELLOSIGN_EVENT_TYPE.SIGNATURE_REQUEST_SIGNED:
            // Notify user of successful signing
            userCtx.setNotificationInformation(
              "Signature request signed successfully. Status changes will be reflected in a few minutes. Refresh your list to get the latest data.",
            )
            break
          case HELLOSIGN_EVENT_TYPE.SIGNATURE_REQUEST_INVALID:
          case HELLOSIGN_EVENT_TYPE.SIGN_URL_INVALID:
          case HELLOSIGN_EVENT_TYPE.UNKNOWN_ERROR:
            userCtx.setNotificationError(
              "Something went wrong while processing your signature request. Please try again later. If the issue persists, please contact support.",
            )
            LogError("Signature-request error", dataObj)
            break
        }
      }
    } catch (error) {
      LogError("Error handling HelloSign event", error)
      userCtx.setNotificationError(
        "An error occurred while processing the HelloSign event. Please contact support if you keep seeing this.",
      )
    }
  }

  const setupSignalRConnection = async () => {
    try {
      const api_url = documentSigningApiUrl()

      // Setup Connection
      const connection = new HubConnectionBuilder()
        .withUrl(`${api_url}/hubs/hellosign`)
        .configureLogging(isTestMode ? LogLevel.Debug : LogLevel.Warning)
        .withAutomaticReconnect() // Automatically reconnect if the connection is lost
        .build()

      // Register client method
      connection.on("HelloSignEvent", (data) => {
        handleHelloSignEvent(data)
      })

      // Start the connection
      await connection.start()

      // Save connection to state
      setConnection(connection)
    } catch (error) {
      LogError("Error setting up SignalR connection", error)
      setConnection(null)
    }
  }

  useEffect(() => {
    if (!connection) {
      setupSignalRConnection()
    }

    // Cleanup connection on unmount
    return () => {
      if (connection) {
        connection.stop()
        LogWarning("Disconnected from HelloSign Hub")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (userCtx.participant_organizations.length > 0) {
      const mobileWidth = window.innerWidth < 786

      // Define filters
      setDocFilters([
        // Bar filters
        {
          key: "organization_filter",
          label: "Organization",
          defaultValue: FilterDefaults.multiselect,
          currentValue: FilterDefaults.multiselect,
          type: FilterTypes.multiselect,
          location: mobileWidth ? FilterLocation.panel : FilterLocation.bar,
          options: userCtx.participant_organizations.map((org) => ({
            name: org.organization_name,
            code: org.organization_id,
          })),
          placeholder: "Select organization",
          showClear: false,
          showFilter: false,
          showBarLabel: true,
        },
        {
          key: "status_filter",
          label: "Due status",
          defaultValue: "awaiting_signature",
          currentValue: "awaiting_signature",
          type: FilterTypes.dropdown,
          location: mobileWidth ? FilterLocation.panel : FilterLocation.bar,
          options: [
            { name: "Awaiting signature", code: "awaiting_signature" },
            { name: "Complete", code: "signed" },
            { name: "Declined", code: "declined" },
          ],
          placeholder: "Select status",
          showClear: true,
          showFilter: false,
          showBarLabel: true,
        },
        {
          key: "date_requested_filter",
          label: "Date requested",
          defaultValue: FilterDefaults.calendar,
          currentValue: FilterDefaults.calendar,
          type: FilterTypes.calendar,
          location: mobileWidth ? FilterLocation.panel : FilterLocation.bar,
          placeholder: "Select request date",
          showTime: false,
          showIcon: false,
          rangeSelect: true,
          showBarLabel: true,
        },
        {
          key: "text_search",
          label: "Title, documents or metadata",
          placeholder: "Search title, documents or metadata",
          defaultValue: FilterDefaults.text,
          currentValue: FilterDefaults.text,
          type: FilterTypes.text,
          location: FilterLocation.bar,
          showBarLabel: true,
        },
      ])

      // Set initial/default filters
      setAppliedFilters(defaultFilters)

      getRequests(currentPage, pageSize, defaultFilters)
    }

    if (!helloSignConfig) {
      getHelloSignConfig()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.participant_organizations])

  const getHelloSignConfig = async () => {
    try {
      const config = await docSigningApiHelloSignConfigGet(
        userCtx.tokens.getDocumentSignToken(),
      )
      setHelloSignConfig(config)
    } catch (error) {
      LogError(error)
      userCtx.setNotificationError(
        "Unable to retrieve Hello Sign configuration. See console for details.",
      )
    }
  }

  const downloadRequestDocument = async (signatureRequestId) => {
    setLoadingDropboxSign(true)
    try {
      const res = await docSigningApiSignatureRequestsDownloadGet(
        userCtx.tokens.getDocumentSignToken(),
        signatureRequestId,
      )
      save(res, `${signatureRequestId}.pdf`)
      setLoadingDropboxSign(false)
    } catch (error) {
      userCtx.setNotificationError(
        `Unable to download signature request documents. See console for details.`,
      )
      LogError("Unable to download signature request documents", error)
      setLoadingDropboxSign(false)
    }
  }

  function blockZooming() {
    const viewport = document.querySelector("meta[name=viewport]")
    if (viewport) {
      let content = viewport.getAttribute("content") || ""
      const newContent = content.split(/,\s?/)
      // Prevent browsers from automatically zooming into text fields
      if (!content.includes("maximum-scale=1")) {
        newContent.push("maximum-scale=1")
        content = newContent.join(", ")
      }
      if (content !== viewport.getAttribute("content")) {
        viewport.setAttribute("content", content)
      }
    }
  }

  const signDocument = async (signatureRequest) => {
    setLoadingDropboxSign(true)
    try {
      const res = await docSigningApiSignURLGet(
        userCtx.tokens.getDocumentSignToken(),
        signatureRequest.mySignatureRequest.signatureId,
      )

      if (res && res.embedded.signUrl) {
        const client = new HelloSign({
          clientId: helloSignConfig.clientId,
          skipDomainVerification: helloSignConfig.mode === "Test",
        })

        blockZooming() // work-around for mobile signing
        client.open(res.embedded.signUrl)
        client.on("close", () =>
          userCtx.setNotificationInformation(
            "Please note: It might take a few minutes for status changes to reflect accurately.",
          ),
        )
      }
      setLoadingDropboxSign(false)
    } catch (error) {
      let message = "Unable to sign the request. See console for details."

      if (
        error.message
          ?.toLowerCase()
          .includes("this request has already been signed")
      ) {
        message =
          "Unable to sign the request. This request has already been signed. Please refresh your list"
      }

      if (
        error.message?.toLowerCase().includes("request cannot be signed yet")
      ) {
        message =
          "This request cannot be signed yet. There are other signers that need to sign before you."
      }

      userCtx.setNotificationError(message)
      LogError(message, error)
      setLoadingDropboxSign(false)
    }
  }

  function row1Items() {
    return [
      {
        value: "organizationName",
        label: "Organization",
      },
      {
        value: "title",
        label: "Title",
      },
      {
        value: "requestCreatorName",
        label: "Requested by",
      },
      {
        value: "signers",
        label: "Signers",
      },
    ]
  }

  function row2Items() {
    return [
      {
        value: "requestCreatedAtLocal",
        label: "Request date",
      },
      {
        value: "status",
        label: "Status",
      },
      {
        value: "completedDateLocal",
        label: "Date completed",
      },
      {
        value: "orderText",
        label: "Order",
      },
    ]
  }

  const getItemValue = (item, value) => {
    if (value === "signers") {
      return item[value].split(",").map((signer) => {
        return (
          <div key={signer.trim()}>
            <span
              className="pi pi-user"
              style={{ marginRight: "8px", fontSize: "12px", color: "#999999" }}
            />
            {signer.trim()}
          </div>
        )
      })
    }

    if (value === "status") {
      const statusLabel = item.status.split(" (")[0] // Get the main status label
      const statusCount = item.status.split(" (")[1]?.replace(")", "") || "" // Get the count if available

      return (
        <div>
          <div
            className={
              documentStyles[
                `status_${statusLabel.toLowerCase().replace(/\s/g, "_")}`
              ]
            }
          >
            {statusLabel}
          </div>
          <div className={documentStyles.status_count}>{statusCount}</div>
        </div>
      )
    }

    return item[value] || "N/A" // Default handling for other values
  }

  return (
    <div>
      <div className="main-content">
        <AppBreadcrumb items={[{ label: "Document Signing" }]} />
        <StandardPageHeader
          title="Document Signing"
          refreshData={() => getRequests(currentPage, pageSize)}
        />

        <hr className={"line"} />
        <br />

        <div className="pb-5">
          {/* Filters */}
          <CustomFilter
            filters={docFilters}
            setFilters={setDocFilters}
            applyFilters={ApplyFiltersAndSorting}
          />
          <br />

          {/* Loader */}
          {loading && <LoaderMedium />}

          {/* No requests found */}
          {!loading && documents?.items?.length === 0 && (
            <NoData
              title="No results found for the selected filters"
              subTitle=""
            />
          )}

          {/* Requests */}
          {!loading && documents?.items?.length > 0 && (
            <>
              {documents.items.map((request) => (
                <div
                  key={request.signatureRequestId}
                  className={documentStyles.requestContainer}
                >
                  <div className={documentStyles.requestContainerInnerLeft}>
                    <div className={documentStyles.itemRow1}>
                      {row1Items().map((item) => (
                        <div key={item.value} className={documentStyles.item}>
                          <span>{item.label}</span>
                          <label>{getItemValue(request, item.value)}</label>
                        </div>
                      ))}
                    </div>

                    <div className={documentStyles.itemRow2}>
                      {row2Items().map((item) => (
                        <div key={item.value} className={documentStyles.item}>
                          <span>{item.label}</span>
                          <label>{getItemValue(request, item.value)}</label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={documentStyles.requestContainerInnerRight}>
                    {!request.completedDate && request.canSignNow && (
                      <Button
                        icon="pi pi-pen-to-square"
                        label="Sign"
                        severity="primary"
                        rounded
                        style={{ paddingLeft: "30px", paddingRight: "30px" }}
                        onClick={() => signDocument(request)}
                        loading={loadingDropboxSign}
                        disabled={
                          loadingDropboxSign ||
                          !userCtx.isMicroserviceEnabled(
                            MICROSERVICE_IDS.DOCUMENT_SIGN_ID,
                            request.organizationId,
                          )
                        }
                      />
                    )}

                    {request.completedDate && (
                      <Button
                        icon="pi pi-download"
                        label="Download"
                        severity={loadingDropboxSign ? "primary" : "secondary"}
                        outlined
                        rounded
                        onClick={() =>
                          downloadRequestDocument(request.signatureRequestId)
                        }
                        loading={loadingDropboxSign}
                        disabled={loadingDropboxSign}
                      />
                    )}
                  </div>
                </div>
              ))}

              <Paginator
                first={documents?.pageNum || 1}
                rows={documents?.pageSize || 10}
                totalRecords={documents?.numResults || 0}
                onPageChange={(event) => {
                  setCurrentPage(event.page + 1)
                  setPageSize(event.rows)
                  getRequests(event.page + 1, event.rows)
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
